// d:\chinaone-frontend\src\main.ts
import { createApp, watch } from "vue";
import "bootstrap/dist/css/bootstrap.min.css";
import "pretendard/dist/web/variable/pretendardvariable-dynamic-subset.css";


import "@/assets/css/UserLayout_new.css";
import "@/assets/css/UserStyle_new.css";
import "@/assets/css/UserMain_new.css";
import "@/assets/css/blue_new.css";
import App from "./App.vue";
import router from "./router";
import { createPinia } from "pinia";
import piniaPluginPersistedstate from "pinia-plugin-persistedstate";
import {
  ElAlert, ElAutocomplete, ElAvatar, ElBadge, ElButton, ElCard, ElCheckbox,
  ElCol, ElDatePicker, ElDescriptions, ElDescriptionsItem, ElDialog,
  ElDropdown, ElDropdownItem, ElDropdownMenu, ElEmpty, ElForm, ElFormItem,
  ElIcon, ElImage, ElInput, ElInputNumber, ElLink, ElLoading, ElMessage,
  ElMessageBox, ElOption, ElOptionGroup, ElPagination, ElPopover, ElRadio,
  ElRadioGroup, ElRow, ElScrollbar, ElSelect, ElStep, ElSteps, ElSwitch,
  ElTable, ElTableColumn, ElTabPane, ElTabs, ElTag, ElTimePicker, ElTooltip,
  ElTree, ElUpload,
} from "element-plus";
import "element-plus/dist/index.css";
import "@/assets/css/element-override.css";
import {
  ArrowDown, ArrowLeft, ArrowRight, ArrowUp, ArrowUpBold, Bell, Bottom, Box,
  Calendar, CaretRight, ChatDotRound, CircleCheck, CircleCheckFilled, Clock,
  Close, CopyDocument, CreditCard, DataAnalysis, Delete, Discount, Document,
  DocumentCopy, Download, Edit, FolderOpened, Hide, InfoFilled, Iphone,
  Loading, Location, Lock, Menu, Message, Minus, Money, OfficeBuilding,
  Phone, PhoneFilled, Picture, Plus, Promotion, QuestionFilled, Search,
  Setting, Ship, ShoppingBag, SwitchButton, Tickets, TrendCharts, Upload,
  User, Van, VideoPlay, View, Wallet, Warning, WarningFilled,
} from "@element-plus/icons-vue";
import i18n from "./locales";
import preventClick from "@/directives/preventClick";
import PageTitle from "@/components/common/PageTitle.vue";
import { useUserStore } from "@/stores/user";
import { useDictStore } from "@/stores/dictStore";
import { applyRouteSeo } from "@/utils/seo";

document.documentElement.lang = String(i18n.global.locale.value || "ko-KR");
router.isReady().then(() => {
  watch(
    [() => router.currentRoute.value.fullPath, () => String(i18n.global.locale.value)],
    ([location, locale]) => applyRouteSeo(location, locale),
    { immediate: true },
  );
}).catch((error) => {
  console.error("[SEO] 初始路由加载失败:", error);
});

const staleAssetReloadKey = "chinaone:stale-asset-reload";
const chunkLoadErrorPattern =
  /ChunkLoadError|Loading chunk .* failed|Failed to fetch dynamically imported module|error loading dynamically imported module|Importing a module script failed/i;

const reloadAfterStaleAssetError = () => {
  if (sessionStorage.getItem(staleAssetReloadKey)) return;
  sessionStorage.setItem(staleAssetReloadKey, window.location.href);
  window.location.reload();
};

// 页面跨版本长时间停留后，旧 hash 的懒加载文件可能已经不存在。
window.addEventListener("vite:preloadError", (event) => {
  event.preventDefault();
  reloadAfterStaleAssetError();
});

router.onError((error) => {
  const message = error instanceof Error
    ? `${error.name}: ${error.message}`
    : String(error);
  if (chunkLoadErrorPattern.test(message)) {
    reloadAfterStaleAssetError();
  }
});

const app = createApp(App);
app.component("PageTitle", PageTitle);

const globalIcons = {
  ArrowDown, ArrowLeft, ArrowRight, ArrowUp, ArrowUpBold, Bell, Bottom, Box,
  Calendar, CaretRight, ChatDotRound, CircleCheck, CircleCheckFilled, Clock,
  Close, CopyDocument, CreditCard, DataAnalysis, Delete, Discount, Document,
  DocumentCopy, Download, Edit, FolderOpened, Hide, InfoFilled, Iphone,
  Loading, Location, Lock, Menu, Message, Minus, Money, OfficeBuilding,
  Phone, PhoneFilled, Picture, Plus, Promotion, QuestionFilled, Search,
  Setting, Ship, ShoppingBag, SwitchButton, Tickets, TrendCharts, Upload,
  User, Van, VideoPlay, View, Wallet, Warning, WarningFilled,
};
for (const [key, component] of Object.entries(globalIcons)) {
  app.component(key, component);
}

const pinia = createPinia();
pinia.use(piniaPluginPersistedstate);
app.use(pinia);

const userStore = useUserStore();
const dictStore = useDictStore();
userStore.initFromStorage();
// 同源窗口共享登录状态；读取当前值而不是事件快照，避免登录时的临时删除覆盖新登录。
window.addEventListener("storage", (event) => {
  if (event.storageArea !== window.localStorage || (event.key !== "user" && event.key !== null)) return;
  const shared = userStore.readSharedUserInfo();
  if (shared !== undefined) userStore.syncSharedUserInfo(shared);
});
const initialRoute = router.resolve(window.location.pathname);
const skipGlobalDataRefresh = initialRoute.meta.skipGlobalDataRefresh === true;
if (userStore.userInfo?.id && !skipGlobalDataRefresh) {
  userStore.refreshAccountOverview().catch((error) => {
    console.error("[App] 刷新用户账户汇总信息失败:", error);
  });
  dictStore.fetchCtmsOptions(true).catch((error) => {
    console.error("[App] 刷新通关类目失败:", error);
  });
}

app.use(router);
// Keep only the Element Plus components used by templates; v-loading and
// programmatic message APIs still need their plugins installed globally.
const elementPlusComponents = [
  ElAlert, ElAutocomplete, ElAvatar, ElBadge, ElButton, ElCard, ElCheckbox,
  ElCol, ElDatePicker, ElDescriptions, ElDescriptionsItem, ElDialog,
  ElDropdown, ElDropdownItem, ElDropdownMenu, ElEmpty, ElForm, ElFormItem,
  ElIcon, ElImage, ElInput, ElInputNumber, ElLink, ElOption, ElOptionGroup,
  ElPagination, ElPopover, ElRadio, ElRadioGroup, ElRow, ElScrollbar,
  ElSelect, ElStep, ElSteps, ElSwitch, ElTable, ElTableColumn, ElTabPane,
  ElTabs, ElTag, ElTimePicker, ElTooltip, ElTree, ElUpload,
  ElLoading, ElMessage, ElMessageBox,
];
for (const component of elementPlusComponents) {
  app.use(component);
}
app.use(i18n);
app.directive("prevent-click", preventClick);
app.mount("#app");

// 当前版本路由成功初始化后解除刷新保护，供下一次真实版本更新使用。
router.isReady().then(() => {
  sessionStorage.removeItem(staleAssetReloadKey);
}).catch((error) => {
  console.error("[Router] 初始路由加载失败:", error);
});
